import { AppAction, AppLinkTargetCapability, Application } from '../../apis/eols-app-link/eols-app-link.constants';
import { AssessmentStatusDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { getMedEdQuestionBankAppLinkBody } from '../../constants/med-ed-isbns.constants';
import { isInstructor } from '../../utilities/common.utilities';
import {
  AssignmentTypeStrategy, CoursePlanClickSettings, CoursePlanPerformanceReportClickProps, CoursePlanTitleClickProps
} from '../model';

type StudentLaunchProps = Pick<CoursePlanTitleClickProps, 'assignment' | 'defArgs' | 'sharedAppLinkParams'>;

// A student resumes the assessment (or opens its results when completed) in CKSA, like the self-study launches.
const launchStudentAssessment = async ({ assignment, defArgs, sharedAppLinkParams }: StudentLaunchProps) => {
  const { userId, navigateToApp, fetchRecentAssessmentOfAssignment, postCreateAssessmentFromAssignment } = defArgs.props;
  // First launch: there is no assessment yet, so create one for the student and resume it, like the other Course Plan launches.
  // The service can answer "none" with an empty body (not null), so test for a real assessment id, not only nullish.
  const recentAssessment = await fetchRecentAssessmentOfAssignment(userId, assignment.id);
  const assessment = recentAssessment?.id
    ? recentAssessment
    : await postCreateAssessmentFromAssignment({ assignmentId: assignment.id, userId: Number(userId) }, assignment.maxRetries ?? 0);
  const isCompleted = assessment.status === AssessmentStatusDto.COMPLETED;
  navigateToApp({
    app: Application.EHE_MEDICAL_ASSESSMENT,
    action: isCompleted ? AppAction.ASSESSMENT_PERFORMANCE_VIEW : AppAction.ASSESSMENT_START,
    body: {
      ...sharedAppLinkParams,
      targetCapability: isCompleted ? AppLinkTargetCapability.ASSESSMENT_RESULT_VIEW : AppLinkTargetCapability.TEST_PLAYER,
      // TODO(post-Alpha SHER-10999): remove this line once the token carries the real ISBNs (the CK app then needs no questionBankIsbns).
      ...getMedEdQuestionBankAppLinkBody(),
      assessmentId: String(assessment.id)
    },
    includeLinkHash: true
  });
};

// MCQ assignments are created and edited in the Medical Assessment app (CKSA), so create/edit only redirect, like EAQ.
// Assignment overview handlers are intentionally not defined here (MCQ performance page is SHER-11016).
export const mcqQuiz: AssignmentTypeStrategy = {
  // Instructors are redirected to the assignment in CKSA with ASSIGNMENT_EDIT, like the edit click.
  // Students launch their assessment in CKSA (see launchStudentAssessment).
  coursePlanTitleClick: async (props: CoursePlanTitleClickProps) => {
    const { defArgs, sharedAppLinkParams } = props;
    const { userRole, navigateToApp } = defArgs.props;
    if (isInstructor(userRole)) {
      navigateToApp({
        app: Application.EHE_MEDICAL_ASSESSMENT,
        action: AppAction.ASSIGNMENT_EDIT,
        body: { ...sharedAppLinkParams, targetCapability: AppLinkTargetCapability.ASSIGNMENT_BUILDER },
        includeLinkHash: true
      });
      return;
    }
    await launchStudentAssessment(props);
  },
  // Instructors open the CKSA assignment performance page (full page, same tab); the role comes from the token, so no userId in the body.
  // CKSA does not check the role for Courseware launches, so a student must never reach it: students get their own assessment, like the title click.
  coursePlanPerformanceReportClick: async (props: CoursePlanPerformanceReportClickProps) => {
    const { defArgs, sharedAppLinkParams } = props;
    const { userRole, navigateToApp } = defArgs.props;
    if (isInstructor(userRole)) {
      navigateToApp({
        app: Application.EHE_MEDICAL_ASSESSMENT,
        action: AppAction.ASSIGNMENT_PERFORMANCE_VIEW,
        body: { ...sharedAppLinkParams, targetCapability: AppLinkTargetCapability.FACULTY_PERFORMANCE },
        includeLinkHash: true
      });
      return;
    }
    await launchStudentAssessment(props);
  },
  coursePlanClickSettings: (props: CoursePlanClickSettings) => {
    const { assignment, defArgs, sharedAppLinkParams } = props;
    const { navigateToApp } = defArgs.props;
    const action = assignment ? AppAction.ASSIGNMENT_EDIT : AppAction.ASSIGNMENT_CREATE;
    navigateToApp({
      app: Application.EHE_MEDICAL_ASSESSMENT,
      action,
      body: { ...sharedAppLinkParams, targetCapability: AppLinkTargetCapability.ASSIGNMENT_BUILDER },
      includeLinkHash: true
    });
  }
};
